import { useState } from "react";
import { Formik, Form, Field, FieldArray } from "formik";
import _ from "lodash";
import * as Yup from "yup";
import { Input } from "../Form/Input/Input";
import { UploadImageCard } from "./UploadImageCard/UploadImageCard";
import { Accordion } from "./Accordion/Accordion";
import { TextArea } from "../Form/TextArea/TextArea";
import { PrimaryButton } from "../UI/PrimaryButton/PrimaryButton";
import { Step } from "./Step/Step";
import { AddStepButton } from "./AddStepButton/AddStepButton";
import { PearlsAndPitfalls } from "./PearlsAndPitfalls/PearlsAndPitfalls";
import { References } from "./References/References";
import { Consent } from "./Consent/Consent";
import { ContactInfo } from "./ContactInfo/ContactInfo";
import { Loader } from "../UI/Loader/Loader";
import { ErrorMessage } from "../Form/ErrorMessage/ErrorMessage";
import styles from "./ContributeForm.module.scss";
import { Camera } from "react-feather";

const Schema = Yup.object().shape({
  title: Yup.string().required("Required"),
  authors: Yup.string().required("Required"),
  introduction: Yup.string().required("Required"),
  beforeImage: Yup.object().shape({
    file: Yup.mixed().required("Before image is required"),
  }),
  afterImage: Yup.object().shape({
    file: Yup.mixed().required("After image is required"),
  }),
  steps: Yup.array()
    .of(
      Yup.object().shape({
        image: Yup.object().shape({
          file: Yup.mixed().required("Image is required"),
        }),
        title: Yup.string().required("Title is required"),
        description: Yup.string().required("Description is required"),
      })
    )
    .required("Required")
    .min(3, "You must have minimum 3 steps"),
  consentImageFile: Yup.mixed().test(
    "consentImageFile",
    "Required",
    function (value) {
      const { consentName } = this.parent;
      if (!consentName) return value != null;
      return true;
    }
  ),
  consentName: Yup.mixed().test("consentName", "Required", function (value) {
    const { consentImageFile } = this.parent;
    if (!consentImageFile) return value != null;
    return true;
  }),
  contact: Yup.object().shape({
    name: Yup.string().required("Required"),
    workplace: Yup.string().required("Required"),
    education: Yup.string().required("Required"),
    email: Yup.string().email("Must be a valid email").required("Required"),
  }),
});

export const ContributeForm = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const initialValues = {
    title: "",
    authors: "",
    introduction: "",
    beforeImage: { file: null },
    afterImage: { file: null },
    steps: [
      {
        image: { file: null },
        title: "",
        description: "",
      },
      {
        image: { file: null },
        title: "",
        description: "",
      },
      {
        image: { file: null },
        title: "",
        description: "",
      },
    ],
    pearls: {
      bullets: [""],
    },
    pitfalls: {
      bullets: [""],
    },
    references: {
      bullets: [""],
    },
    consentImage: { file: null },
    consentImageFile: null, // Only for validation
    consentName: "",
    contact: {
      name: "",
      workplace: "",
      education: "",
      email: "",
    },
  };

  const submitHandler = async (formValues) => {
    try {
      setLoading(true);
      const imagePaths = ["beforeImage", "afterImage", "consentImage"];
      formValues.steps?.forEach((step, index) =>
        imagePaths.push(`steps[${index}].image`)
      );

      const newFormValues = await uploadImages(formValues, imagePaths);
      await uploadFormData(newFormValues);
      setSuccess(true);
      window.location.href = `${process.env.REACT_APP_URL}/success`;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const uploadFormData = async (formValues) => {
    return new Promise((resolve, reject) => {
      fetch(`${process.env.REACT_APP_URL}/wp-json/api/add-your-case`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formValues),
      })
        .then((response) => {
          return response.json();
        })
        .then((json) => resolve(json))
        .catch(() => {
          reject("ContributeForm.jsx - uploadFormData() - Error");
        });
    });
  };

  const uploadImages = async (formValues, paths) =>
    new Promise((resolve, reject) => {
      const newFormValues = _.cloneDeep(formValues);

      const uploadPromises = paths.map((path) => {
        const image = _.get(newFormValues, `${path}`);
        const file = image?.file;
        if (!file) return null;
        return uploadImage(file);
      });

      Promise.all(uploadPromises)
        .then((imageDataList) => {
          paths.forEach((path, index) => {
            const image = _.get(newFormValues, `${path}`);
            if (!image) return;
            Object.assign(image, imageDataList[index]);
          });
          resolve(newFormValues);
        })
        .catch(() => {
          reject("ContributeForm.jsx - uploadImages() - Error");
        });
    });

  const uploadImage = (file) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("image-file", file);
      fetch(`${process.env.REACT_APP_URL}/wp-json/api/media`, {
        method: "POST",
        body: formData,
      })
        .then((response) => response.json())
        .then((json) => {
          const id = json?.req?.data?.id;
          const url = json?.req?.data?.url;
          resolve({ id, url });
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitHandler}
      className={styles.root}
      validationSchema={Schema}
    >
      {({ values, errors }) => (
        <Form className={styles.root}>
          <Field
            name="title"
            as={Input}
            className={styles.title}
            placeholder="Type title here..."
          />
          <ErrorMessage name="title" />

          <Field
            name="authors"
            as={Input}
            className={styles.authors}
            placeholder="Type author names here..."
          />
          <ErrorMessage name="authors" />

          <Field
            name="introduction"
            as={TextArea}
            placeholder="Type patient medical history here..."
          />
          <ErrorMessage name="introduction" />

          <UploadImageCard slug="beforeImage" title="Photo before surgery" />
          <ErrorMessage name="beforeImage.file" />

          <Accordion title="Patient examination" slug="patient-examination" />
          <ErrorMessage name="patient-examination.description" />

          <Accordion
            title="Pre-Operative Considerations"
            slug="pre-operative-considerations"
          />

          <br />

          <div className={styles.headerWithIcon}>
            <div className={styles.iconContainer}>
              <Camera />
            </div>
            <div className={styles.headerContainer}>
              <h1 className="ignore-stroke">Step-by-step</h1>
              <p>Procedure photos and description</p>
            </div>
          </div>
          <hr />

          <FieldArray name="steps">
            {(arrayHelpers) => (
              <>
                {values.steps && values.steps.length > 0
                  ? values.steps.map((step, index) => (
                      <Step
                        slug={`steps.${index}`}
                        index={index}
                        key={index}
                        onRemove={() => {
                          arrayHelpers.remove(index);
                        }}
                      />
                    ))
                  : null}
                <div className="d-flex justify-content-center">
                  <AddStepButton onClick={() => arrayHelpers.push({})}>
                    Add more photos
                  </AddStepButton>
                </div>
                <ErrorMessage>
                  {typeof errors.steps === "string" ? errors.steps : " "}
                </ErrorMessage>
              </>
            )}
          </FieldArray>

          <UploadImageCard slug="afterImage" title="Photo after surgery" />
          <ErrorMessage name="afterImage.file" />

          <PearlsAndPitfalls title="Pearls" slug="pearls" />
          <PearlsAndPitfalls title="Pitfalls" slug="pitfalls" />
          <Accordion title="Post-Operative Plan" slug="post-operative-plan" />
          <References title="References" slug="references" />
          <hr />

          <h2 className={styles.sectionHeader}>Patient Consent Form</h2>
          <Consent />
          <ErrorMessage name="consentName" />
          <ErrorMessage name="consentImage.file" />
          <hr />

          <h2 className={styles.sectionHeader}>Author Contact Information</h2>
          <ContactInfo />

          <hr />
          {error && (
            <p>
              Something went wrong. Please try again or contact{" "}
              <a href="mailto:handbookplasticsurgery@gmail.com">
                handbookplasticsurgery@gmail.com
              </a>
            </p>
          )}
          {Object.keys(errors).length > 0 && (
            <ErrorMessage>Check the form for errors and try again</ErrorMessage>
          )}
          <PrimaryButton type="submit" block>
            {loading || success ? <Loader /> : "Submit"}
          </PrimaryButton>
          <br />
          <br />
        </Form>
      )}
    </Formik>
  );
};
